import {
  Box,
  Divider,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { useAdminUserContext } from '../../core/hooks/useAdminUserContext'
import { maskPercentage } from '../../masks/masks'
import InfoInputAdornment from '../Inputs/InfoInputAdornment'
import { useEffect, useState } from 'react'

type TFeeField =
  | 'capturePixFee'
  | 'captureCardFee'
  | 'captureBoletoFee'
  | 'mdrPixFee'
  | 'mdrCardFee'
  | 'mdrBoletoFee'

type TFeeType = 'currency' | 'percentage'
type TFeeMode = 'normal' | 'custom'

const feeModeOptions = [
  { value: 'normal', label: 'Normal' },
  { value: 'custom', label: 'Personalizado' },
]

const onlyDigits = (value: string) => value.replace(/\D/g, '')

const maskDecimalInput = (value?: number | null, scale = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) return ''

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
  })
}

const parseMaskedDecimalInput = (value: string, scale = 2) => {
  const digits = onlyDigits(value)

  if (!digits) return 0

  const numericValue = Number(digits) / Math.pow(10, scale)
  return Number(numericValue.toFixed(scale))
}

const applyDecimalMask = (value: string, scale = 2) => {
  const digits = onlyDigits(value)

  if (!digits) return ''

  const numericValue = Number(digits) / Math.pow(10, scale)

  return numericValue.toLocaleString('pt-BR', {
    minimumFractionDigits: scale,
    maximumFractionDigits: scale,
  })
}

interface FeeConfigRowProps {
  label: string
  field: TFeeField
  type: TFeeType
  infoTitle: string
}

const FeeConfigRow = ({ label, field, type, infoTitle }: FeeConfigRowProps) => {
  const { tenantSettings, setTenantSettings } = useAdminUserContext()

  const currentValue = tenantSettings?.[field] as number | null | undefined
  const isCustom = currentValue !== null && currentValue !== undefined

  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (!isCustom) {
      setInputValue('')
      return
    }

    if (type === 'percentage') {
      setInputValue(maskPercentage(currentValue ?? 0))
      return
    }

    setInputValue(maskDecimalInput(currentValue ?? 0))
  }, [currentValue, isCustom, type])

  const handleChangeMode = (mode: TFeeMode) => {
    if (!tenantSettings) return

    setTenantSettings({
      ...tenantSettings,
      [field]: mode === 'normal' ? null : 0,
    })
  }

  const handleChangeValue = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!tenantSettings) return

    const rawValue = e.target.value

    if (type === 'percentage') {
      const masked = applyDecimalMask(rawValue)
      const parsed = parseMaskedDecimalInput(rawValue)

      setInputValue(masked)
      setTenantSettings({
        ...tenantSettings,
        [field]: parsed,
      })
      return
    }

    const masked = applyDecimalMask(rawValue)
    const parsed = parseMaskedDecimalInput(rawValue)

    setInputValue(masked)
    setTenantSettings({
      ...tenantSettings,
      [field]: parsed,
    })
  }

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ py: 1.25 }}
    >
      <Box sx={{ minWidth: { xs: '100%', md: 90 } }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
      </Box>

      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel id={`${field}-mode-label`}>Regra</InputLabel>
        <Select
          labelId={`${field}-mode-label`}
          label="Regra"
          value={isCustom ? 'custom' : 'normal'}
          onChange={(e) => handleChangeMode(e.target.value as TFeeMode)}
        >
          {feeModeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Fade in={isCustom} mountOnEnter unmountOnExit>
        <Box sx={{ flex: 1 }}>
          <OutlinedInput
            fullWidth
            size="small"
            type="text"
            value={inputValue}
            onChange={handleChangeValue}
            placeholder="0,00"
            endAdornment={<InfoInputAdornment title={infoTitle} />}
          />
        </Box>
      </Fade>
    </Stack>
  )
}

interface FeeSectionCardProps {
  title: string
  subtitle?: string
  rows: Array<{
    label: string
    field: TFeeField
    type: TFeeType
    infoTitle: string
  }>
}

const FeeSectionCard = ({ title, subtitle, rows }: FeeSectionCardProps) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        <Divider />

        <Stack divider={<Divider flexItem />} spacing={0}>
          {rows.map((row) => (
            <FeeConfigRow
              key={row.field}
              label={row.label}
              field={row.field}
              type={row.type}
              infoTitle={row.infoTitle}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  )
}

export { FeeSectionCard }
